import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const HowWeWork: React.FC = () => {
  const { t } = useTranslation();

  const steps = [
    {
      key: 'ideation',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      number: '01'
    },
    {
      key: 'creation',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      number: '02'
    },
    {
      key: 'curation',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      number: '03'
    },
    {
      key: 'preparation',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
      ),
      number: '04'
    }
  ];

  return (
    <section id="how-we-work" className="py-24 bg-dark-primary relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Elegant separator */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '120px', opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/50 mx-auto mb-8"
          />

          <h2 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-light text-cream mb-6 tracking-wide">
            {t('howWeWork.title')}
          </h2>
          <p className="text-lg text-cream-dark max-w-3xl mx-auto font-light leading-relaxed">
            {t('howWeWork.subtitle')}
          </p>

          {/* Bottom separator */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '80px', opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/30 mx-auto mt-8"
          />
        </motion.div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <motion.div
              key={step.key}
              className="text-center group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              {/* Step number */}
              <div className="relative mb-8">
                <div className="absolute -top-4 -left-4 text-gold/20 font-playfair text-6xl font-light">
                  {step.number}
                </div>
                
                {/* Icon container */}
                <div className="relative z-10 w-20 h-20 mx-auto border border-dark-border/30 bg-dark-surface flex items-center justify-center group-hover:border-gold/50 group-hover:bg-gold/5 transition-all duration-500">
                  <div className="text-gold group-hover:text-gold/90 transition-colors duration-300">
                    {step.icon}
                  </div>
                </div>
              </div>

              {/* Step content */}
              <div className="space-y-4">
                <h3 className="font-playfair text-xl text-cream group-hover:text-gold/90 transition-colors duration-300">
                  {t(`howWeWork.steps.${step.key}.title`)}
                </h3>
                
                {/* Elegant divider */}
                <div className="w-12 h-px bg-gold/30 mx-auto group-hover:w-16 group-hover:bg-gold/50 transition-all duration-300"></div>
                
                <p className="text-cream-dark font-light leading-relaxed">
                  {t(`howWeWork.steps.${step.key}.description`)}
                </p>
              </div>

              {/* Connection line (desktop only) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-10 left-full w-12 h-px bg-dark-border/20 transform translate-y-10 -translate-x-6">
                  <div className="absolute right-0 top-0 w-2 h-2 bg-gold/30 rounded-full transform -translate-y-1"></div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Decorative elements */}
        <div className="absolute top-1/4 right-10 w-32 h-32 bg-gold/5 rounded-full filter blur-2xl animate-float"></div>
        <div className="absolute bottom-1/4 left-10 w-24 h-24 bg-gold/5 rounded-full filter blur-2xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>
    </section>
  );
};

export default HowWeWork;